import { keyBy } from 'lodash';
import { Moment } from 'moment';
import * as qs from 'qs';

import { AddCapabilities, SetCardPrograms } from '@Actions/cardsActions';
import { ReceiveLinkedAccounts, RequestLinkedAccounts } from '@Actions/currentUserActions';
import { store } from '@ReactStore/store';

import { ICardExtension } from '@ClientModels/card_extension';
import { AccountCapabilities } from '@Shared/interfaces/CardsGatewayInterfaces';
import { ProgramCapabilities } from '@Shared/interfaces/CardsInterfaces';
import { ILinkedAccountStats } from '@Shared/linked_account_stats';
import { ILinkedAccountModel } from '@Shared/models';

import { Api as apiService } from '../api';

export type ProgramCardCounts = Record<string, { assigned: number; unassigned: number }>;

export type FinicityAccount = {
  _id: string;
  _user: string;
  balance: {
    available: string;
    current: string;
  };
  meta: {
    name: string;
    number: string;
  };
  type: string;
  institutionLoginId: string;
};

export type FinicityConnectBody = {
  institution_id: string;
  customer_id: string;
};

export type CompanyCardProgramsParams = {
  overrideStore?: boolean;
  onlyPending?: boolean;
  includeStatementCycle?: boolean;
  includeSubaccounts?: boolean;
};

export type StatementDateBody = { statement_date: number | null };

export abstract class LinkedAccountsApiService {
  /**
   * @summary Gets all alternate emails for the currency user, either from the Redux Store or via the API
   * @param {boolean} overrideStore if true, circumvents checking the store and makes an API request
   *
   */
  static get(overrideStore: boolean = false): Promise<ILinkedAccountModel[]> {
    if (!overrideStore) {
      const currentUser = store.getState().currentUser;

      if (currentUser && currentUser.cards) {
        return Promise.resolve(currentUser.cards);
      }
    }

    // request action
    store.dispatch(new RequestLinkedAccounts());
    return apiService
      .get('/api/web/accounts/cards')
      .then((response) => response.json() as Promise<ILinkedAccountModel[]>)
      .then((linkedAccounts) => {
        // success action
        store.dispatch(new ReceiveLinkedAccounts(linkedAccounts));

        return linkedAccounts;
      });
  }

  static getPendingOrDisconnectedPlaidAccount(destinationType: 'company' | 'user') {
    return apiService.get(`/api/web/accounts/plaid/${destinationType}`).then((response) => response?.json() as Promise<any>);
  }

  static getPlaidAccount() {
    return apiService.get('/api/web/accounts/company/plaid_account').then((response) => response?.json());
  }

  static getUserPlaidAccount() {
    return apiService.get('/api/web/accounts/user/plaid_account').then((response) => response?.json());
  }

  static cancel(linkedAccountId: string) {
    return apiService
      .patch(`/api/web/accounts/${linkedAccountId}/cancel`)
      .then((response) => response.json() as any)
      .then((linkedAccount) => linkedAccount);
  }

  static getCompanyCardPrograms({
    overrideStore,
    onlyPending,
    includeStatementCycle,
    includeSubaccounts = true
  }: CompanyCardProgramsParams): Promise<ILinkedAccountModel[]> {
    if (!overrideStore && !onlyPending) {
      const state = store.getState();
      const programs = state.cards && state.cards.programs;
      if (programs.length) {
        return programs;
      }
    }

    return apiService
      .get(
        `/api/web/accounts/subaccounts?includePending=${onlyPending}&includeStatementCycle=${includeStatementCycle}&includeSubaccounts=${includeSubaccounts}`
      )
      .then((response) => response.json() as any)
      .then((cardPrograms: ILinkedAccountModel[]) => {
        if (onlyPending) {
          return cardPrograms.filter((x) => x.state === 'pending');
        }
        store.dispatch(new SetCardPrograms(cardPrograms));
        return cardPrograms;
      });
  }

  static async getCompanyCorporateCards(): Promise<ILinkedAccountModel[]> {
    const url = '/api/web/accounts/company/cards?includes[user]=&includes[parent_account]';
    return await apiService.responseToJSONAndToastError(apiService.get(url));
  }

  static async getExpiringCards(filters: {
    allowanceEndDateBefore: string;
    allowanceEndDateAfter: string;
  }): Promise<{ numberOfExpiringCards: number }> {
    let params = new URLSearchParams({
      'filters[allowanceEndDateBefore]': filters.allowanceEndDateBefore,
      'filters[allowanceEndDateAfter]': filters.allowanceEndDateAfter
    });
    const url = `/api/web/accounts/cards/expiring?${params}`;
    return await apiService.responseToJSONAndToastError(apiService.get(url));
  }

  static async updateCompanyCardPrograms(cards: ICardExtension[]) {
    const response = await apiService.post('/api/web/accounts/subaccounts', cards);

    return await response.json();
  }

  static getAccountCapabilities(cardProgramId: string): Promise<AccountCapabilities> {
    return apiService
      .get(`/api/web/accounts/cardprograms/${cardProgramId}/capabilities`)
      .then((response) => response.json() as any)
      .then((resp) => resp.capabilities);
  }

  static getGatewayProgramsCapabilities(overrideStore?: boolean) {
    return apiService
      .get(`/api/web/accounts/capabilities`)
      .then((res) => res.json() as any)
      .then((res) => res.capabilities as ProgramCapabilities[])
      .then((capabilities: ProgramCapabilities[]) => {
        if (overrideStore) {
          store.dispatch(new AddCapabilities(keyBy(capabilities, 'programId')));
        }
        return capabilities;
      });
  }

  static getGatewaySessionUrl(cardProgramId: string, redirectUrl: string) {
    return apiService
      .get(`/api/web/accounts/cardprograms/${cardProgramId}/session?&redirect_url=${encodeURI(redirectUrl)}`)
      .then((response) => response.json() as any)
      .then((resp) => resp.sessionUrl);
  }

  static connectGatewayCardProgram(connectTo: string, connectionType: string | null, redirectUrl: string) {
    const postBody = {
      connectTo,
      connectionType,
      redirectUrl
    };

    return apiService.post('/api/web/accounts/cardprograms?provider=gateway', postBody).then((response) => response.json() as any);
  }

  static async addCards(cardProgramId: string, userData: { user_ids: string[] }) {
    const response = await apiService.post(`/api/web/accounts/cardprograms/${cardProgramId}`, userData);

    return await response.json();
  }

  static async addProgram(bankData: { institution_type: string; name: string; refresh_start_date: number }) {
    const response = await apiService.post('/api/web/accounts/cardprograms', bankData);

    return await response.json();
  }

  static suspendCard(linkedAccountId: string) {
    return apiService
      .post(`/api/accounts/${linkedAccountId}/suspend`)
      .then((response) => response.json() as any)
      .then((linkedAccount) => linkedAccount);
  }

  static unsuspendCard(linkedAccountId: string) {
    return apiService
      .post(`/api/accounts/${linkedAccountId}/unsuspend`)
      .then((response) => response.json() as any)
      .then((linkedAccount) => linkedAccount);
  }

  static async deleteCard(linkedAccountId: string) {
    const response = await apiService.delete(`/api/web/accounts/${linkedAccountId}`);
    return await response.json();
  }

  static async skipCardTransactions(beforeDate: Moment | Date, accountIds: string[]) {
    await apiService.post('/api/web/accounts/cards/skiptransactions', {
      before: beforeDate.valueOf(),
      account_ids: accountIds
    });
  }

  static suspendProgram(linkedAccountId: string): Promise<ILinkedAccountModel> {
    return apiService.responseToJSONAndToastError(apiService.patch(`/api/web/accounts/${linkedAccountId}/pause`));
  }

  static deleteProgram(linkedAccountId: string) {
    return apiService.responseToJSONAndToastError(apiService.delete(`/api/web/accounts/${linkedAccountId}`));
  }

  static async updateCardProgramName(cardProgramId: string, name: string): Promise<ILinkedAccountModel> {
    return apiService.responseToJSON(apiService.patch(`/api/web/accounts/cardprograms/${cardProgramId}/name`, { name: name }));
  }

  static async updateCardProgramStatementDate(cardProgramId: string, data: StatementDateBody): Promise<ILinkedAccountModel> {
    return apiService.responseToJSONAndToastError(apiService.patch(`/api/web/accounts/cardprograms/${cardProgramId}/statement_date`, data));
  }

  static getProgramCardCounts(): Promise<ProgramCardCounts> {
    return apiService.responseToJSONAndToastError(apiService.get('/api/web/accounts/subaccounts/counts'));
  }

  static removeCardErrors(linkedAccountId: string) {
    return apiService.responseToJSONAndToastError(apiService.delete(`/api/web/accounts/${linkedAccountId}/errors`));
  }

  static searchCardsForProgram(
    parentLinkedAccountIds: string | string[], // Card program ID
    limit: number,
    offset: number,
    sortField?: string,
    sortDirection?: 'asc' | 'desc',
    filters?: Record<string, string>,
    search: string = '',
    includeAllowance?: boolean,
    includeSharedLink?: boolean,
    includeUnexpensedTransactionCount?: boolean,
    types?: string[]
  ): Promise<{ count: number; rows: ILinkedAccountModel[] }> {
    const searchParams = new URLSearchParams({
      limit: limit?.toString(),
      offset: offset?.toString(),
      search: search?.toString()
    });

    if (sortField) {
      searchParams.append('sortField', sortField);
      searchParams.append('sortDirection', sortDirection);
    }

    if (filters) {
      searchParams.append('filters', JSON.stringify(apiService.parseFilters(filters)));
    }

    const include = {
      allowance: includeAllowance,
      json_image: includeSharedLink,
      unexpensed_transaction_count: includeUnexpensedTransactionCount
    };
    searchParams.append('include', JSON.stringify(include));

    if (typeof parentLinkedAccountIds === 'string') {
      searchParams.append('programIds[]', parentLinkedAccountIds);
    } else {
      parentLinkedAccountIds?.forEach((parentLinkedAccountId) => {
        searchParams.append('programIds[]', parentLinkedAccountId);
      });
    }

    if (types) {
      types.forEach((type) => searchParams.append('types[]', type));
    }

    return apiService.responseToJSONAndToastError(apiService.get(`api/web/accounts/cardprograms/cards?${searchParams}`));
  }

  static getCardsPaginated({
    limit,
    offset,
    sortField = 'created_at',
    sortDirection = 'asc',
    includeAllowance = true,
    state,
    type
  }: {
    limit: number;
    offset: number;
    sortField?: string;
    sortDirection?: 'asc' | 'desc';
    includeAllowance?: boolean;
    state?: ILinkedAccountModel['state'];
    type?: ILinkedAccountModel['type'][];
  }): Promise<{ count: number; rows: ILinkedAccountModel[] }> {
    const queryParams = qs.stringify({
      limit: limit?.toString(),
      offset: offset?.toString(),
      state: state,
      sortField,
      sortDirection,
      include: includeAllowance ? 'allowance' : undefined,
      type
    });

    return apiService.responseToJSONAndToastError(apiService.get(`api/web/accounts/cards/paginate?${queryParams}`));
  }

  static async getStatistics(): Promise<ILinkedAccountStats> {
    const response = await apiService.get(`api/web/accounts/cards/stats`);

    return await response.json();
  }

  static async getActiveCardCountStatistics(): Promise<{ activeCardsCount: string; activeCardsLastMonthCount: string }> {
    const response = await apiService.get(`api/web/accounts/cards/active_stats`);

    return await response.json();
  }

  static async getStatisticsByCardType(): Promise<ILinkedAccountStats> {
    const response = await apiService.get(`api/web/accounts/cards/stats_by_type`);

    return await response.json();
  }

  static async getBalance(): Promise<{ balance: number }> {
    const response = await apiService.get(`api/web/accounts/cards/balance`);

    return await response.json();
  }

  static async setCard(cardData: {}, options: { subaccounts?: boolean }) {
    let query = '';

    if (options?.subaccounts) {
      query += '&subaccounts=1';
    }

    const response = await apiService.post(`/api/web/accounts/cards?${query}`, cardData);

    return await response.json();
  }

  static reconnectAccount(accountId: string, requestBody: FinicityConnectBody) {
    return apiService.patch(`/api/web/accounts/${accountId}`, requestBody).then((response) => response.json() as any);
  }

  static getFinicityAccounts(
    requestBody: FinicityConnectBody,
    options?: { subaccounts?: boolean }
  ): Promise<{ state?: string; accounts: FinicityAccount[] }> {
    const queryParams = qs.stringify({
      subaccounts: options?.subaccounts ? '1' : undefined
    });

    return apiService.post(`/api/web/accounts/cards/auth?${queryParams}`, requestBody).then((response) => response.json() as any);
  }

  static makeDemoTransaction(linkedAccountId: string) {
    return apiService.responseToJSONAndToastError(apiService.post(`/api/web/accounts/${linkedAccountId}/makeDemoTransaction`));
  }
}
